/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */

import { groupBy } from 'lodash';
import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const contentFont = 10;
const pageMargins = [28.8, 100, 32.8, 25.92];
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 10],
  },
  subheader: {
    fontSize: 16,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  tableExample: {
    margin: [0, 5, 0, 15],
  },
  tableHeader: {
    bold: true,
    fontSize: 13,
    color: 'black',
  },
};
const valueFormatter = (value = 0) => {
  if (value < 0) {
    return `($${value
      .toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
      .substring(2)})`;
  }
  return parseFloat(value)?.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

const numberFormatter = (value = 0) => {
  if (value < 0) {
    return `(${parseFloat(value)?.toLocaleString('en-US').substring(1)})`;
  }
  return parseFloat(value)?.toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

const getHeader = (currentPage, pageCount, data, options, mfgIdIndex = null) => {
  const {
    quarter,
    rebateProgramName,
    prinDate,
    requestedBy,
    reportName,
    mfgName,
    mfgId,
    isMultipleMfg,
    labelerCodeToMfgIdMap,
    labelerCodeToNameMap,
  } = data;
  
  let currentMfgId = mfgId;
  let currentMfgName = mfgName;
  
  if (window.__pdfLabelerCodes && window.__pdfLabelerCodes.length > 0) {
    const labelerCodesCount = window.__pdfLabelerCodes.length;
    const pagesPerGroup = Math.ceil(pageCount / labelerCodesCount);
    const currentLabelerIndex = Math.floor((currentPage - 1) / pagesPerGroup);
    
    if (currentLabelerIndex < window.__pdfLabelerCodes.length) {
      const currentLabelerCode = window.__pdfLabelerCodes[currentLabelerIndex];
      currentMfgId = currentLabelerCode;
      currentMfgName = labelerCodeToNameMap && labelerCodeToNameMap[currentLabelerCode]
        ? labelerCodeToNameMap[currentLabelerCode]
        : currentLabelerCode;
    }
  }
  
  const header = {
    alignment: 'center',
    fontSize: 10,
    margin: [28.8, 20, 28.8, 25.92],
    style: 'tableExample',
    table: {
      widths: [400, 400, 400],
      body: [
        [
          {
            text: [{ text: 'Requested By: ', bold: true }, requestedBy],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: reportName,
            fontSize: 12,
            bold: true,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Date Printed: ', bold: true }, prinDate],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: '',
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: rebateProgramName,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
        [
          {
            text: [{ text: 'Quarter: ', bold: true }, quarter],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
          },
          {
            text: [{ text: 'Mfg: ', bold: true }, currentMfgId],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            bold: true,
          },
          {
            text: [{ text: 'Name: ', bold: true }, currentMfgName],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
  return header;
};

const getHeaderColumns = (columns, type) => {
  const border = [true, true, true, true];
  const headers = [
    {
      text: '',
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
      border,
    },
  ];

  for (let i = 0; i < columns.length; i += 1) {
    headers.push({
      text: columns[i],
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
      border,
    });
  }

  return headers;
};

const getTableRows = (
  providerListData,
  providerListTableHead,
  claimListData,
  providerClaimTableHead
) => {
  const groupedData = claimListData;
  const mainTableHeader = providerListTableHead.map((item) => item.headerName);
  let subTableHeader = providerClaimTableHead.map((item) => item.headerName);

 
  const border = [true, true, true, true];
  const dataToReturn = [];
  for (let i = 0; i < providerListData.length; i += 1) {
    const mainHeaders = getHeaderColumns(mainTableHeader, 'main');
    dataToReturn.push(mainHeaders);
    const {
      labelerCode,
      productCode,
      packageSize,
      drugName,
      statusCode,
      adjustedUnitRebateAmt,
      adjustedUnits,
      paidUnits,
      paidAmt,
      disputedUnits,
      drugSeqNumber,
    } = providerListData[i];

    const rosipadCell = {
      text: '',
      fontSize: contentFont - 1,
      alignment: 'center',
      color: 'black',
      border: [true, true, true, true],
    };

    const rosiRow = mainHeaders.map((h, idx) => {
      if (idx === 0) {
        return {
          text: 'ROSI Information',
          fontSize: contentFont,
          alignment: 'left',
          color: 'black',
          border,
          bold: true,
        };
      }
      const headerName = (mainTableHeader[idx - 1] || '').toString().toLowerCase();

      if (headerName.includes('ndc') && !headerName.includes('status')) {
        return {
          text: `${labelerCode}-${productCode}-${packageSize}`,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        };
      }
      if (headerName.includes('drug') && headerName.includes('name')) {
        return {
          text: drugName,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        };
      }
      if (headerName.includes('ndc status') || (headerName.includes('status') && !headerName.includes('ura')) ) {
        return {
          text: statusCode,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        };
      }
      if (headerName.includes('adjust') && headerName.includes('ura')) {
        return {
          text: adjustedUnitRebateAmt || '0.0000',
          fontSize: contentFont - 1,
          alignment: 'right',
          color: 'black',
          border,
        };
      }
      if (headerName.includes('unit') && headerName.includes('adjust')) {
        return {
          text: adjustedUnits || '0.0000',
          fontSize: contentFont - 1,
          alignment: 'right',
          color: 'black',
          border,
        };
      }
      if (headerName.includes('units') && headerName.includes('paid')) {
        return {
          text: numberFormatter(paidUnits),
          fontSize: contentFont - 1,
          alignment: 'right',
          color: 'black',
          border,
        };
      }
      if (headerName.includes('amount') || headerName.includes('paid amt') || headerName.includes('amount paid')) {
        return {
          text: valueFormatter(paidAmt || 0),
          fontSize: contentFont - 1,
          alignment: 'right',
          color: paidAmt < 0 ? 'red' : 'black',
          border,
        };
      }
      if (headerName.includes('disput') || headerName.includes('units disputed')) {
        return {
          text: disputedUnits || '0.0000',
          fontSize: contentFont - 1,
          alignment: 'right',
          color: 'black',
          border,
        };
      }

      return rosipadCell;
    });

    dataToReturn.push(rosiRow);
    const claimData = groupedData[drugSeqNumber] || [];

    const subHeaders = getHeaderColumns(subTableHeader, 'sub');

    if (claimData.length) {
      dataToReturn.push(subHeaders);
    }

    for (let j = 0; j < claimData.length; j += 1) {
      const {
        labelerCode,
        productCode,
        packageSize,
        drugName,
        invoiceQtr,
        lastUpdateDt,
        checkLogNum,
        ura,
        numberOfUnits,
        unitsDisputedToDate,
        amountPaidThisTransaction,
        unitsPaidToDate,
      } = claimData[j];
      const ndcPqas = `${labelerCode}-${productCode}-${packageSize}`;

      dataToReturn.push([
        {
          text: 'PQAS Information',
          fontSize: contentFont,
          alignment: 'left',
          color: 'black',
          border,
          bold: true,
        },
        {
          text: invoiceQtr,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
        {
          text: lastUpdateDt,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
        {
          text: checkLogNum,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
        {
          text: ura || '0.0000',
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
        {
          text: numberOfUnits,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
        {
          text: unitsPaidToDate,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
        {
          text: valueFormatter(amountPaidThisTransaction || 0),
          fontSize: contentFont - 1,
          alignment: 'right',
          color: amountPaidThisTransaction < 0 ? 'red' : 'black',
          border,
        },
        {
          text: unitsDisputedToDate,
          fontSize: contentFont - 1,
          alignment: 'center',
          color: 'black',
          border,
        },
      ]);
    }


  }
  return dataToReturn;
};
const getTableBody = (
  providerListData,
  providerListTableHead,
  claimListData,
  providerClaimTableHead
) => {
  const providerData = providerListData;

  const groupedByLabelerCode = groupBy(providerData, (item) => item.labelerCode);
  
  let labelerCodes = Object.keys(groupedByLabelerCode);
  labelerCodes = labelerCodes.sort((a, b) => a.localeCompare(b));
  
  window.__pdfLabelerCodes = labelerCodes;
  window.__pdfCurrentLabelerCodeIndex = 0;

  const contentArray = [];

  labelerCodes.forEach((labelerCode, labelerIndex) => {
    let groupData = groupedByLabelerCode[labelerCode];
    
    groupData = groupData.sort((a, b) => {
      const ndcA = `${a.labelerCode}-${a.productCode}-${a.packageSize}`;
      const ndcB = `${b.labelerCode}-${b.productCode}-${b.packageSize}`;
      return ndcA.localeCompare(ndcB);
    });
    
    const rows = getTableRows(
      groupData,
      providerListTableHead,
      claimListData,
      providerClaimTableHead
    );

    const maxCols = rows.reduce((max, row) => {
      if (Array.isArray(row)) return Math.max(max, row.length);
      return max;
    }, 0);

    const widths = maxCols > 0 ? Array(maxCols).fill('*') : ['*'];

    const paddedBody = rows.map((row) => {
      if (!Array.isArray(row)) return row;
      if (row.length === maxCols) return row;
      const padCount = maxCols - row.length;
      const padCell = {
        text: '',
        fontSize: contentFont,
        alignment: 'center',
        color: 'black',
      };
      return [...row, ...Array.from({ length: padCount }, () => padCell)];
    });

    contentArray.push({
      style: 'tableExample',
      color: '#444',
      table: {
        widths,
        body: paddedBody,
      },
    });
    
    if (labelerIndex < labelerCodes.length - 1) {
      contentArray.push({ text: '', pageBreak: 'before' });
    }
  });

  return contentArray;
};
const getPdfData = (
  providerListData,
  providerListTableHead,
  claimListData,
  providerClaimTableHead,
  data,
  options
) => {
  const { pageSize } = options;

  const content = getTableBody(
    providerListData,
    providerListTableHead,
    claimListData,
    providerClaimTableHead
  );
  const currentYear = new Date().getFullYear();
  let docDefinition = {
    header: (currentPage, pageCount) => {
      return getHeader(currentPage, pageCount, data, options);
    },
    footer: () => {
        const footers = [];
        footers.push({
          text:`${currentYear} Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.`,
          fontSize: 8,
          color: '#636363',
          fontFamily: 'Arial',
          alignment: 'center',
        });

      return footers;
    },
    content,
    pageMargins,
    styles,
    pageOrientation: 'landscape',
  };

  if (pageSize && pageSize?.width && pageSize.height) {
    docDefinition = {
      ...docDefinition,
      pageSize: {
        width: pageSize.width,
        height: pageSize.height,
      },
    };
  }
  return docDefinition;
};

const exportRosiPqasPDF = (
  providerListData,
  providerListTableHead,
  claimListData,
  providerClaimTableHead,
  userDefaults,
  options,
  logInUser
) => {
  const { rebateProgramName, quarter, mfgName, mfgId, isMultipleMfg, labelerCodeToMfgIdMap, labelerCodeToNameMap } = options;
  const data = {
    requestedBy:
      userDefaults && userDefaults.userId ? userDefaults.userId : logInUser,
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    reportName: 'ROSI PQAS Detail Report',
    rebateProgramName,
    quarter,
    mfgName,
    mfgId,
    isMultipleMfg,
    labelerCodeToMfgIdMap,
    labelerCodeToNameMap,
  };

  const pdfData = getPdfData(
    providerListData,
    providerListTableHead,
    claimListData,
    providerClaimTableHead,
    data,
    options
  );
  const { fileName } = options;
  pdfMake.createPdf(pdfData).download(`${fileName || 'data'}.pdf`);
};

export default exportRosiPqasPDF;
